import React, { useContext, useEffect, useState } from 'react';
import featuredIcon from '../../../assets/Featured icon.png';
import { Button, Card, Select, Spinner, TextInput } from 'flowbite-react';
import personalCard from '../../../assets/personalcard.png';
import buildingIcon from '../../../assets/building.png';
import { FaArrowRight } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import * as styles from './kyb.styles';
import CustomInput from '../../../components/CustomInputs/CustomInput';
import CustomSelectInput from '../../../components/CustomSelectInput/CustomSelectInput';
import PrivateLayout from '../../../layouts/PrivateLayout/PrivateLayout';
import client from '../../../api/client';
import showToast from '../../../components/CustomToast/toastHelper';
import { UserContext } from '../../../contexts/UserContext';

interface BusinessPayload {
    subsidiaryId: number;
    businessName: string;
    country: string;
    businessType: string;
    businessSize: string;
    businessCategoryId: number;
    referalCode?: string;
    isDeveloper: boolean;
}

interface FormValues {
    businessName: string;
    businessType: string;
    isDeveloper: boolean;
    businessSize: string;
    businessCategoryId: number;
    referralCode: string;
    country: string;
    businessCategory: string;
    countryName:string;
}

interface Country {
    id: number;
    name: string;
    shortName: string;
}

interface BusinessCategory {
    id: number;
    name: string;
}

const KnowYourBusiness = () => {
    const { userDetails } = useContext(UserContext) ?? {}
    let activeSubsidiary = userDetails?.activeSubsidiary ?? {};
    const navigate = useNavigate();
    const [kycDetails, setKycDetails] = useState<any>(null)
    const [businessInfo, setBusinessInfo] = useState<any>(null)
    const [businessCategories, setBusinessCategories] = useState<BusinessCategory[]>([])
    const [businessSizes, setBusinessSizes] = useState<any[]>([])
    const [countries, setCountries] = useState<Country[]>([])
    const [selectedCountry, setSelectedCountry] = useState<string>('')
    const [selectedBusinessCategory, setSelectedBusinessCategory] = useState<string>('')
    const [activateCategorySelection, setActivateCategorySelection] = useState<boolean>(false)
    const [activateCountrySelection, setActivateCountrySelection] = useState<boolean>(false)
    const [formValues, setFormValues] = useState<FormValues>({
        businessName: '',
        businessType: 'Individual',
        isDeveloper: false,
        businessSize: '',
        businessCategoryId: 0,
        referralCode: '',
        country: '',
        countryName:'',
        businessCategory:''
    });
    const [activeCard, setActiveCard] = useState<string>('Individual');
    const [loading, setLoading] = useState<boolean>(true)
    const [submitting, setSubmitting] = useState<boolean>(false)

    useEffect(() => {
        const fetchData = async () => {
            try {
                await Promise.all([
                    fetchCategories(),
                    fetchBusinessSizes(),
                    getCountries(),
                    getKycProfileDetails(),
                    getBusinessInfo()
                ]);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (userDetails?.activeSubsidiary?.businessName) {
            setFormValues(prev => ({
                ...prev,
                businessName: userDetails.activeSubsidiary.businessName
            }));
        }
    }, [userDetails?.activeSubsidiary]);

    useEffect(() => {
        if (kycDetails) {
            
            const selectedCountry = countries.find((c: Country) => c.name === kycDetails.country);
            
            setFormValues(prev => ({
                ...prev,
                businessType: kycDetails.businessType || 'Company',
                countryName: kycDetails.country || '',
                country:selectedCountry?.shortName || ''
            }));
        }
    }, [kycDetails, countries]);

    useEffect(() => {
        if (businessInfo) {
            
            const category = businessCategories.find((c: BusinessCategory) => c.name === businessInfo.businessRegisteredCategory);
            
            if (category) {
                setSelectedBusinessCategory(category.name);
            }
            setFormValues(prev => ({
                ...prev,
                businessSize: businessInfo.businessSize || '',
                isDeveloper: businessInfo.isDeveloper || false,
                referralCode: businessInfo.referralCode || '',
                businessCategoryId: Number(category?.id) || 0,
                businessCategory: businessInfo.businessRegisteredCategory || ''
            }));
        }
    }, [businessInfo]);

    const fetchCategories = async () => {
        try {
            const response = await client.get("/subsidiary/dashboard/business/categories")

            if (response.data.status == "success") {
                setBusinessCategories(response.data.data)
            }
            else {
                console.log(response.data)
            }
        }
        catch (err: any) {
            console.error('Sign in error', err)
        }
    }

    const fetchBusinessSizes = async () => {
        try {
            const response = await client.get("/subsidiary/dashboard/business/sizes");
            if (response.data.status === "success") {
                setBusinessSizes(response.data.data);
            } else {
                console.log(response.data.message);
            }
        } catch (err) {
            console.error('Fetch business sizes error', err);
        }
    };

    const getCountries = async () => {
        try {
            const response = await client.get("/subsidiary/dashboard/service/countries");
            if (response.data.status === "success") {
                setCountries(response.data.data);
            } else {
                console.log(response.data.message);
            }
        } catch (err) {
            console.error('error getting countries', err);
        }
    }
    
    const getKycProfileDetails = async () => {
        try {
            const response = await client.get("/subsidiary/dashboard/kyc/profile/view");
            if (response.data.status === "success") {
                setKycDetails(response.data.data);
            }
        } catch (err) {
            console.error('Error getting KYC profile:', err);
        }
    }

    const getBusinessInfo = async () => {
        try {
            const response = await client.get("/subsidiary/dashboard/onboarding/business/information/view");
            if (response.data.status === "success") {
                setBusinessInfo(response.data.data);
            }
        } catch (err) {
            console.error('Error getting business info:', err);
        }
    }


    const handleCardClick = (businessType: string) => {
        setFormValues({ ...formValues, businessType })
        setActiveCard(businessType);
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormValues({ ...formValues, [name]: value });
    };

    const handleCountryFocus = () => {
        setActivateCountrySelection(true);
    };

    const handleCountryChange = (value: string) => {
        
        const selectedCountry = countries.find((c: Country) => c.shortName === value);
        
        if (selectedCountry) {
            setSelectedCountry(selectedCountry.name);
            setFormValues(prev => ({ 
                ...prev, 
                country: selectedCountry.shortName ,
                countryName: selectedCountry.name

            }));
        }
    };

    const handleCategoryFocus = () => {
        setActivateCategorySelection(true);
    };

    const handleBusinessCategoryChange = (value: string) => {
        
        const selectedCategory = businessCategories.find((c: BusinessCategory) => c.id === Number(value));
        if (selectedCategory) {
            setSelectedBusinessCategory(selectedCategory.name);
            setFormValues(prev => ({ 
                ...prev, 
                businessCategoryId: selectedCategory.id, 
                businessCategory: selectedCategory.name 
            }));
        }
    };

    const handleSubmit = async () => {
        setSubmitting(true)
        let payload: BusinessPayload = {
            subsidiaryId: activeSubsidiary?.id,
            country: formValues.country,
            businessName: formValues.businessName,
            businessType: formValues.businessType,
            businessSize: formValues.businessSize,
            businessCategoryId: Number(formValues.businessCategoryId),
            referalCode: formValues.referralCode,
            isDeveloper: formValues.isDeveloper
        }
        try {
            const response = await client.post("/subsidiary/dashboard/business/get-started", payload);
            showToast(response.data.message, "success")
            navigate('/business/onboarding/kyc')
        } catch (err: any) {
            if (err?.response?.data?.message === 'Please login again') {
                sessionStorage.clear();
                window.location.href = '/';
            } else {
                const message = err?.response?.data?.message;
                console.error('error getting data', err);
                showToast(message, "error");
            }
        }
        finally{
            setSubmitting(false)
        }
    }
    return (
        <PrivateLayout>

                <div className={styles.container}>
                    <p className={styles.homeText}>Home</p>
                    <hr />
                    <div className={styles.gridContainer}>
                        <div>
                            <p className={styles.sectionTitle}>Let's know a bit more about your business</p>
                            <p className={styles.sectionSubtitle}>A few more things to help us set up your dashboard</p>
                        </div>
                        <div className=''>
                            <div>
                                <img src={featuredIcon} alt="Featured Icon" />
                            </div>
                            <div className={styles.imageContainer}>
                                <p className={styles.cardTitle}>Business</p>
                                <p className={styles.cardSubtitle}>Kindly provide us with some business information to get your dashboard ready</p>
                            </div>
                            <div className={styles.imageContainer}>
                                <CustomInput label='Business/Company Name' name='businessName' value={formValues.businessName} placeholder='Enter Business Name' onChange={handleInputChange}
                                />
                            </div>
                            <div className={styles.imageContainer}>
                                <div className="transition-all duration-300 ease-in-out">
                                    {activateCountrySelection ? (
                                        <CustomSelectInput 
                                            items={countries} 
                                            value={formValues.country}
                                            displayKey="name" 
                                            valueKey='shortName'  
                                            label='Where is your business based?' 
                                            className={styles.cardTitle} 
                                            onChange={handleCountryChange}
                                            name="country"
                                        />
                                    ) : (
                                        <CustomInput 
                                            label='Where is your business based?' 
                                            name='country' 
                                            value={formValues.countryName || 'Select a country'} 
                                            onFocus={handleCountryFocus}
                                        />
                                    )}
                                </div>
                            </div>
                            <div className={styles.imageContainer}>
                                <p className={styles.cardTitle}>What type of business do you do?</p>
                                <div className={styles.cardGrid}>
                                    <Card onClick={() => handleCardClick('Individual')} className={activeCard === 'Individual' ? 'border-2 border-custom-button-green hover:border-green-600' : 'hover:border-green-600'}>
                                        <div>
                                            <img src={personalCard} alt="Personal Card" />
                                            <p className={styles.cardTextBold}>Starter/Individual</p>
                                            <p className={styles.cardSubtitle}>An unregistered business e.g freelancers and sole traders</p>
                                        </div>
                                    </Card>
                                    <Card onClick={() => handleCardClick('Company')} className={activeCard === 'Company' ? 'border-2 border-custom-button-green hover:border-green-600' : 'hover:border-2 border-green-600'}>
                                        <div>
                                            <img src={buildingIcon} alt="Building Icon" />
                                            <p className={styles.cardTextBold}>Registered</p>
                                            <p className={styles.cardSubtitle}>A registered business with a corporate bank account</p>
                                        </div>
                                    </Card>
                                </div>
                                <div className={styles.selectGrid}>
                                    <div>
                                        <CustomSelectInput items={businessSizes} value={formValues.businessSize} displayKey='name' label='Business Size' className={styles.cardTitle} onChange={(value) => setFormValues({ ...formValues, businessSize: value })}
                                        />

                                    </div>
                                    <div>
                                        <CustomSelectInput items={["Yes", "No"]} value={formValues.isDeveloper?"Yes":"No"} displayKey='' label='Are you a software developer?' className={styles.cardTitle} onChange={(value) => setFormValues({ ...formValues, isDeveloper: value === 'Yes' })}

                                        />
                                    </div>
                                    <div>
                                        <div className="transition-all duration-300 ease-in-out">
                                            {activateCategorySelection ? (
                                                <CustomSelectInput 
                                                    items={businessCategories} 
                                                    value={String(formValues.businessCategoryId)}
                                                    displayKey='name' 
                                                    valueKey='id' 
                                                    label='Business Category' 
                                                    className={styles.cardTitle} 
                                                    onChange={handleBusinessCategoryChange}
                                                    name="businessCategory"
                                                />
                                            ) : (
                                                <CustomInput 
                                                    label='Business Category' 
                                                    name='businessCategory' 
                                                    value={formValues.businessCategory || 'Select a business category'} 
                                                    onFocus={handleCategoryFocus}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <CustomInput label="Referral Code (Optional)" name='referralCode' onChange={handleInputChange} />
                                    </div>
                                </div>
                            </div>
                            <div className={styles.buttonContainer}>
                                <Button onClick={() => handleSubmit()} className={styles.proceedButton}>
                                    {submitting? <Spinner size='sm'/>:<><p>Proceed to dashboard</p>
                                    <FaArrowRight className={styles.buttonIcon} /></>}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
        </PrivateLayout>
    );
};

export default KnowYourBusiness;
